import { useState } from 'react'

import {
  Button,
  DropdownMenu,
  FilterIcon,
  StatusDot,
  Tooltip,
} from '@buffer-mono/popcorn'

import {
  DATE_RANGES,
  SORT_DIRECTION_LABELS,
  type DateRange,
  type SortDirection,
} from './types'
import { useDelayedHover } from '../hooks/useDelayedHover'

type FilterAndSortMenuProps = {
  sortDirection?: SortDirection
  publishedAt?: DateRange
  onSortDirectionChange: (value: SortDirection) => void
  onPublishedAtChange: (value: DateRange) => void
  onResetFilters: () => void
}

const isValidSortDirection = (value: string): value is SortDirection => {
  return Object.keys(SORT_DIRECTION_LABELS).includes(value)
}

const isValidDateRange = (value: string): value is DateRange => {
  return Object.keys(DATE_RANGES).includes(value)
}

export const FilterAndSortMenu = ({
  sortDirection = 'desc',
  publishedAt = 'alltime',
  onSortDirectionChange,
  onPublishedAtChange,
  onResetFilters,
}: FilterAndSortMenuProps): JSX.Element => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  const [showTooltip, setShowTooltip] = useState(false)

  const { handleMouseEnter, handleMouseLeave } = useDelayedHover({
    onOpen: () => setShowTooltip(true),
    onClose: () => setShowTooltip(false),
    openDelayDuration: 1000,
    closeDelayDuration: 100,
    isOpen: showTooltip,
  })

  // Get active filters count to show on the button
  const getActiveFiltersCount = (): number => {
    let count = 0
    if (sortDirection && sortDirection !== 'desc') count += 1
    if (publishedAt && publishedAt !== 'alltime') count += 1
    return count
  }

  const activeFiltersCount = getActiveFiltersCount()

  // Create type-safe handlers for RadioGroup
  const handleSortChange = (value: string): void => {
    if (!isValidSortDirection(value)) {
      // eslint-disable-next-line no-console
      console.error(
        'Invalid sort direction value:',
        value,
        'Expected one of:',
        Object.keys(SORT_DIRECTION_LABELS),
      )
      return
    }

    onSortDirectionChange(value)
  }

  const handleDateRangeChange = (value: string): void => {
    if (!isValidDateRange(value)) {
      // eslint-disable-next-line no-console
      console.error(
        'Invalid date range value:',
        value,
        'Expected one of:',
        Object.keys(DATE_RANGES),
      )
      return
    }

    onPublishedAtChange(value)
  }

  return (
    <DropdownMenu open={isDropdownOpen} onOpenChange={setIsDropdownOpen}>
      <Tooltip content="Filter comments" open={showTooltip && !isDropdownOpen}>
        <DropdownMenu.Trigger asChild>
          <Button
            size="medium"
            variant="tertiary"
            aria-label="Sort and date filter"
            data-testid="filter-sort-button"
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
          >
            <FilterIcon />
            {activeFiltersCount > 0 && (
              <StatusDot style={{ background: 'var(--color-bg-brand)' }} />
            )}
          </Button>
        </DropdownMenu.Trigger>
      </Tooltip>

      <DropdownMenu.Content align="start" data-no-focus-lock>
        <DropdownMenu.RadioGroup
          label="Sort By"
          value={sortDirection}
          onChange={handleSortChange}
        >
          <DropdownMenu.RadioItem value="desc">Newest</DropdownMenu.RadioItem>
          <DropdownMenu.RadioItem value="asc">Oldest</DropdownMenu.RadioItem>
        </DropdownMenu.RadioGroup>

        <DropdownMenu.Separator />

        <DropdownMenu.RadioGroup
          label="Date Range"
          value={publishedAt}
          onChange={handleDateRangeChange}
        >
          <DropdownMenu.RadioItem value="last7days">
            Last 7 days
          </DropdownMenu.RadioItem>
          <DropdownMenu.RadioItem value="last30days">
            Last 30 days
          </DropdownMenu.RadioItem>
          <DropdownMenu.RadioItem value="last6months">
            Last 6 months
          </DropdownMenu.RadioItem>
          <DropdownMenu.RadioItem value="lastyear">
            Last 12 months
          </DropdownMenu.RadioItem>
          <DropdownMenu.RadioItem value="alltime">
            All time
          </DropdownMenu.RadioItem>
        </DropdownMenu.RadioGroup>
        <DropdownMenu.Separator />
        <DropdownMenu.Item onClick={onResetFilters}>
          Reset filters
        </DropdownMenu.Item>
      </DropdownMenu.Content>
    </DropdownMenu>
  )
}
